
import { useState } from "react";
import { addWeeks, subWeeks, startOfWeek, addDays } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { EventDialog } from "./EventDialog";
import { ScheduleSettings } from "./ScheduleSettings";
import { DayView } from "./components/DayView";
import { WeekView } from "./components/WeekView";
import { MonthView } from "./components/MonthView";
import { CalendarHeader } from "./calendar/CalendarHeader";
import { useCalendarEvents } from "./calendar/useCalendarEvents";
import { useCalendarSettings } from "./calendar/useCalendarSettings";
import { toast } from "sonner";

export function ScheduleCalendar() {
  const [view, setView] = useState("week");
  const [date, setDate] = useState(new Date());
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [showSettings, setShowSettings] = useState(false);
  
  const { settings, fetchSettings } = useCalendarSettings();
  const { events, fetchEvents } = useCalendarEvents(date, view);

  const handleEventClick = (event: any) => {
    setSelectedEvent(event);
    setSelectedDate(undefined);
    setShowEventDialog(true);
  };

  const handleDateClick = (date: Date) => {
    if (!settings) {
      toast.error("Configure primeiro os horários de trabalho");
      return;
    }
    setSelectedDate(date);
    setShowEventDialog(true);
  };

  const handleDeleteEvent = async (event: any) => {
    try {
      if (event.event_type === 'class_request') {
        // It's a schedule request - update status to rejected
        const { error: reqError } = await supabase
          .from('schedule_requests')
          .update({ status: 'rejected' })
          .eq('id', event.id);
        if (reqError) throw reqError;

        // Also delete any related scheduled events
        const { data: relatedEvents } = await supabase
          .from('schedule_events')
          .select('id')
          .or(`related_id.eq.${event.id},and(student_id.eq.${event.student_id},start_time.eq.${event.start_time})`);

        if (relatedEvents && relatedEvents.length > 0) {
          await supabase
            .from('schedule_events')
            .delete()
            .in('id', relatedEvents.map(e => e.id));
        }

        toast.success("Pedido rejeitado com sucesso");
      } else {
        // It's a scheduled event - also update any related request
        if (event.related_id) {
          await supabase
            .from('schedule_requests')
            .update({ status: 'rejected' })
            .eq('id', event.related_id);
        }

        const { error } = await supabase.from("schedule_events").delete().eq("id", event.id);
        if (error) throw error;
        toast.success("Evento apagado com sucesso");
      }

      fetchEvents();
      setSelectedEvent(null);
      setShowEventDialog(false);
    } catch (error) {
      toast.error("Erro ao apagar evento");
      console.error("Error:", error);
    }
  };

  const getEventColor = (eventType: string, status?: string) => {
    if (status === 'pending') {
      return "bg-red-100 text-red-900 border border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800/50";
    }
    if (status === 'approved') {
      return "bg-green-100 text-green-900 border border-green-200 dark:bg-green-900/30 dark:text-green-200 dark:border-green-800/50";
    }
    switch (eventType) {
      case "training_session":
        return "bg-primary/90 text-primary-foreground hover:bg-primary dark:bg-primary/70";
      case "assessment":
        return "bg-accent/90 text-accent-foreground hover:bg-accent dark:bg-accent/70 dark:text-white";
      case "class_request":
        return "bg-red-100 text-red-900 border border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800/50";
      default:
        return "bg-muted/90 text-muted-foreground hover:bg-muted dark:bg-gray-700/70 dark:text-gray-200";
    }
  };

  const getTimeSlots = () => {
    if (!settings) return [];
    const slots = [];
    const startHour = parseInt(settings.work_start_time.split(':')[0]);
    const endHour = parseInt(settings.work_end_time.split(':')[0]);
    
    for (let hour = startHour; hour <= endHour; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      if (settings.slot_duration <= 30) {
        slots.push(`${hour.toString().padStart(2, '0')}:30`);
      }
    }
    return slots;
  };

  const isWorkingDay = (day: Date) => {
    if (!settings) return false;
    const dayNumber = day.getDay();
    return settings.working_days.includes(dayNumber);
  };

  const getWeekDays = () => {
    const startDate = startOfWeek(date, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(startDate, i)).filter(isWorkingDay);
  };

  return (
    <div className="space-y-4">
      <CalendarHeader
        view={view}
        setView={setView}
        settings={settings}
        onCreateEvent={() => {
          setSelectedDate(new Date());
          setShowEventDialog(true);
        }}
        onPreviousWeek={() => setDate(subWeeks(date, 1))}
        onNextWeek={() => setDate(addWeeks(date, 1))}
        onSettingsClick={() => setShowSettings(!showSettings)}
      />

      {showSettings ? (
        <ScheduleSettings
          onSave={() => {
            setShowSettings(false);
            fetchSettings();
            setView("week");
          }}
        />
      ) : (
        <div className="rounded-lg p-4 shadow-sm bg-gray-50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700">
          {view === "week" && (
            <WeekView
              settings={settings}
              date={date}
              events={events}
              timeSlots={getTimeSlots()}
              weekDays={getWeekDays()}
              handleDateClick={handleDateClick}
              handleEventClick={handleEventClick}
              handleDeleteEvent={handleDeleteEvent}
              getEventColor={getEventColor}
            />
          )}
          {view === "day" && (
            <DayView
              settings={settings}
              date={date}
              events={events}
              timeSlots={getTimeSlots()}
              handleDateClick={handleDateClick}
              handleEventClick={handleEventClick}
              handleDeleteEvent={handleDeleteEvent}
              getEventColor={getEventColor}
            />
          )}
          {view === "month" && (
            <MonthView
              date={date}
              events={events}
              handleDateClick={handleDateClick}
              handleEventClick={handleEventClick}
              handleDeleteEvent={handleDeleteEvent}
              getEventColor={getEventColor}
              setDate={setDate}
            />
          )}
        </div>
      )}

      <EventDialog
        isOpen={showEventDialog}
        onClose={() => {
          setShowEventDialog(false);
          setSelectedEvent(null);
          setSelectedDate(undefined);
        }}
        selectedDate={selectedDate}
        selectedEvent={selectedEvent}
        onEventSaved={fetchEvents}
        onEventDeleted={handleDeleteEvent}
      />
    </div>
  );
};
